// Updated: 2026-08-13
import type { SearchResponse, SearchProjectsResponse, MaterialFilters } from '@mrb91002/shared-types'

import { API_BASE } from '../config'

import { PublicApiError } from './errors'

/**
 * Pattern search (lambda-main `GET /api/search?q=…`).
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`
 * (react-query-browse-search, Req 6.1, 6.2); it is spread conditionally, so a caller that omits it
 * makes a byte-identical request to before — which is what keeps the un-migrated callers
 * (`PatternSelectionModal`, `MobilePatternSearchModal`) working untouched. The URL, every query
 * parameter including the four conditional material filters, and the success parsing are unchanged.
 *
 * Note the `if (materialFilters?.brand)` truthiness guards below: they are why the query layer
 * NORMALIZES filters before keying. `{}`, `{brand: ''}`, and `{brand: '   '}` all produce the same URL
 * here, so they must produce the same cache key there (design § D2).
 *
 * On failure this now throws a status-bearing `PublicApiError` — same message as before — so the shared
 * retry predicate can read the HTTP status and never retry a 4xx (Req 6.3, 6.4).
 */
export async function searchPatterns(
  query: string,
  limit: number = 20,
  offset: number = 0,
  materialFilters?: MaterialFilters,
  opts?: { signal?: AbortSignal },
): Promise<SearchResponse> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
    offset: offset.toString(),
  })

  // Add material filters if provided
  if (materialFilters?.category) params.append('materialCategory', materialFilters.category)
  if (materialFilters?.brand) params.append('materialBrand', materialFilters.brand)
  if (materialFilters?.color) params.append('materialColor', materialFilters.color)
  if (materialFilters?.name) params.append('materialName', materialFilters.name)

  console.log('[searchPatterns] Query params:', params.toString())
  const response = await fetch(`${API_BASE}/search?${params}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })

  if (!response.ok) {
    throw new PublicApiError('Pattern search failed', response.status)
  }

  return response.json() as Promise<SearchResponse>
}

/**
 * Craft-project search (lambda-main `GET /api/projects/search?q=…`).
 *
 * `opts.signal` and the status-bearing error are additive, as in {@link searchPatterns}. The response
 * carries `{from, size, total}` rather than a `hasMore` flag, so the query layer derives end-of-list as
 * `(from + size) < total` — exactly what `SearchResults` computed inline before the migration.
 */
export async function searchProjects(
  query: string,
  limit: number = 20,
  offset: number = 0,
  opts?: { signal?: AbortSignal },
): Promise<SearchProjectsResponse> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
    offset: offset.toString(),
  })

  console.log('[searchProjects] Query params:', params.toString())
  const response = await fetch(`${API_BASE}/projects/search?${params}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })

  if (!response.ok) {
    throw new PublicApiError('Project search failed', response.status)
  }

  return response.json() as Promise<SearchProjectsResponse>
}

/**
 * Tag search (lambda-main `GET /api/search?tag=…`). `opts.signal` and the status-bearing error are
 * additive, as in {@link searchPatterns}.
 */
export async function searchByTag(
  tag: string,
  limit: number = 20,
  offset: number = 0,
  opts?: { signal?: AbortSignal },
): Promise<SearchResponse> {
  const params = new URLSearchParams({
    tag: tag,
    limit: limit.toString(),
    offset: offset.toString(),
  })

  const response = await fetch(`${API_BASE}/search?${params}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })

  if (!response.ok) {
    throw new PublicApiError('Tag search failed', response.status)
  }

  return response.json() as Promise<SearchResponse>
}

/**
 * Difficulty search (lambda-main `GET /api/search?difficulty=…`). `opts.signal` and the status-bearing
 * error are additive, as in {@link searchPatterns}.
 */
export async function searchByDifficulty(
  difficulty: string,
  limit: number = 20,
  offset: number = 0,
  opts?: { signal?: AbortSignal },
): Promise<SearchResponse> {
  const params = new URLSearchParams({
    difficulty: difficulty,
    limit: limit.toString(),
    offset: offset.toString(),
  })

  const response = await fetch(`${API_BASE}/search?${params}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })

  if (!response.ok) {
    throw new PublicApiError('Difficulty search failed', response.status)
  }

  return response.json() as Promise<SearchResponse>
}
